import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Card,
    CardContent,
    Divider,
    FormControlLabel,
    IconButton,
    Switch,
    Typography,
} from '@mui/material';
import ChevronDownIcon from '@heroicons/react/24/solid/ChevronDownIcon';
import TrashIcon from '@heroicons/react/24/solid/TrashIcon';
import { FunctionComponent } from 'react';
import { ResolvedWidget, SharedDesign } from '@/pages/widgetEditor/types';
import { designParamNames, humanizeLabel } from '@/pages/widgetEditor/utils';
import { WidgetDesignFields } from '@/pages/widgetEditor/components/WidgetDesignFields';

interface WidgetConfigCardProps {
    resolvedWidgets: ResolvedWidget[];
    design: SharedDesign;
    onRemoveWidget: (instanceId: string) => void;
    onOverrideChange: (instanceId: string, name: string, value: string) => void;
    onToggleOverride: (instanceId: string, enabled: boolean) => void;
}

/**
 * One section per added instance. The design itself lives in the shared Design
 * card; what appears here is the opt-in escape hatch for the rare widget that
 * should look different from the rest of the page.
 */
export const WidgetConfigCard: FunctionComponent<WidgetConfigCardProps> = ({
    resolvedWidgets,
    design,
    onRemoveWidget,
    onOverrideChange,
    onToggleOverride,
}) => (
    <Card>
        <CardContent
            sx={{
                display: 'flex',
                alignItems: 'center',
            }}
        >
            <Typography variant='h6'>Widget Konfiguration</Typography>
        </CardContent>
        <Divider sx={{ width: '100%' }} />

        {resolvedWidgets.length === 0 ? (
            <Box sx={{ p: 2 }}>
                <Typography color='text.secondary' variant='body2'>
                    Noch keine Widgets hinzugefügt. Klicke links auf ein Widget,
                    um es hinzuzufügen.
                </Typography>
            </Box>
        ) : (
            resolvedWidgets.map(({ instance, widget }) => {
                const designNames = designParamNames(widget);
                const isOverriding = instance.hasDesignOverride;

                return (
                    <Box key={instance.instanceId}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 6,
                                py: 3,
                            }}
                        >
                            <Typography variant='subtitle2'>
                                {humanizeLabel(widget.widget)}
                            </Typography>
                            <IconButton
                                size='small'
                                aria-label='Widget entfernen'
                                onClick={() =>
                                    onRemoveWidget(instance.instanceId)
                                }
                            >
                                <TrashIcon
                                    style={{
                                        width: 18,
                                        height: 18,
                                    }}
                                />
                            </IconButton>
                        </Box>

                        {designNames.length === 0 ? (
                            <Box sx={{ px: 6, pb: 3 }}>
                                <Typography
                                    color='text.secondary'
                                    variant='body2'
                                >
                                    Keine anpassbaren Felder.
                                </Typography>
                            </Box>
                        ) : (
                            <Accordion disableGutters elevation={0} square>
                                <AccordionSummary
                                    expandIcon={
                                        <ChevronDownIcon
                                            style={{ width: 18, height: 18 }}
                                        />
                                    }
                                    sx={{ px: 6 }}
                                >
                                    <Typography
                                        variant='body2'
                                        color='text.secondary'
                                    >
                                        Abweichendes Design
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ p: 0 }}>
                                    <Box sx={{ px: 6, pb: 2 }}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={isOverriding}
                                                    onChange={(event) =>
                                                        onToggleOverride(
                                                            instance.instanceId,
                                                            event.target.checked
                                                        )
                                                    }
                                                />
                                            }
                                            label='Vom gemeinsamen Design abweichen'
                                        />
                                    </Box>

                                    {/*
                                     * While the switch is off the controls
                                     * show the shared values, disabled — so
                                     * it is visible what the widget looks
                                     * like without having to guess. The
                                     * stored overrides are kept either way,
                                     * so toggling is not destructive.
                                     */}
                                    <WidgetDesignFields
                                        names={designNames}
                                        getParam={(name) =>
                                            widget.parameters[name]
                                        }
                                        getValue={(name) => {
                                            const fallback =
                                                design[name] ??
                                                widget.parameters[name]
                                                    .default ??
                                                '';
                                            return isOverriding
                                                ? (instance.designOverrides[
                                                      name
                                                  ] ?? fallback)
                                                : fallback;
                                        }}
                                        disabled={!isOverriding}
                                        onChange={(name, next) =>
                                            onOverrideChange(
                                                instance.instanceId,
                                                name,
                                                next
                                            )
                                        }
                                    />
                                </AccordionDetails>
                            </Accordion>
                        )}
                        <Divider sx={{ width: '100%' }} />
                    </Box>
                );
            })
        )}
    </Card>
);
